import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Leaf } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/measures", label: "Measures" },
  { to: "/solar-calculator", label: "Solar Calculator" },
  { to: "/grants-finance", label: "Grants & Finance" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/join-network", label: "Join Our Network" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 backdrop-blur-xl bg-brand-cream/85 ${
        scrolled ? "border-b border-stone-200/60 py-3 shadow-sm" : "py-5"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <span className="w-9 h-9 rounded-full bg-brand flex items-center justify-center">
            <Leaf className="w-5 h-5 text-brand-cream" />
          </span>
          <span className="font-outfit font-bold text-lg leading-tight text-brand">
            Network<span className="text-brand-accent"> Survey</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}-link`}
              className={({ isActive }) =>
                `font-figtree text-sm font-medium transition-colors hover:text-brand-accent ${
                  isActive ? "text-brand-accent" : "text-brand"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="header-cta-button"
            className="hidden sm:inline-flex bg-brand-accent text-white hover:bg-brand-accenthover transition-colors rounded-full px-6 py-2.5 text-sm font-medium"
          >
            Free Survey
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-brand p-1"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden mt-3 px-6 pb-6 bg-brand-cream/95 backdrop-blur-xl border-t border-stone-200">
          <div className="flex flex-col gap-1 pt-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `py-3 px-2 rounded-lg font-medium ${isActive ? "text-brand-accent bg-white" : "text-brand"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" className="mt-3 text-center bg-brand-accent text-white rounded-full px-6 py-3 font-medium">
              Request Free Survey
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
